// TeamComparison.js - Main team comparison component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getUserTeamsForComparison, 
  compareTeamScores, 
  validateTeamComparison 
} from '../utils/teamComparison';
import { 
  prepareLineChartData, 
  prepareSpiderChartData, 
  prepareComparisonTableData 
} from '../utils/chartDataPrep';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import './TeamComparison.css';

const TeamComparison = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  
  const [teams, setTeams] = useState([]);
  const [selectedTeam1, setSelectedTeam1] = useState('');
  const [selectedTeam2, setSelectedTeam2] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [chartView, setChartView] = useState('line'); // line, radar, table
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && userProfile) {
      loadTeams();
    }
  }, [user, userProfile]);

  const loadTeams = async () => {
    setLoading(true);
    setError('');
    
    try {
      const userTeams = await getUserTeamsForComparison(user.email, userProfile.organizationId);
      
      if (userTeams.length < 2) {
        setError('Je hebt minimaal 2 relaties met responses nodig om te kunnen vergelijken.');
        setTeams([]);
      } else {
        setTeams(userTeams);
      }
      
    } catch (err) {
      console.error('Error loading teams:', err);
      setError('Fout bij laden relaties: ' + err.message);
    }
    
    setLoading(false);
  };

  const handleCompareTeams = async () => {
    if (!selectedTeam1 || !selectedTeam2 || selectedTeam1 === selectedTeam2) {
      setError('Selecteer twee verschillende relaties om te vergelijken.');
      return;
    }

    setComparing(true);
    setError('');

    try {
      // Validate access
      const validation = await validateTeamComparison(selectedTeam1, selectedTeam2, user.email);
      
      if (!validation.isValid) {
        setError(validation.message);
        setComparing(false);
        return;
      }

      // Get team data
      const team1Data = teams.find(t => t.id === selectedTeam1);
      const team2Data = teams.find(t => t.id === selectedTeam2);

      if (!team1Data || !team2Data) {
        setError('Een van de geselecteerde relaties kon niet worden gevonden.');
        setComparing(false);
        return;
      }

      // Perform comparison
      const comparison = compareTeamScores(team1Data, team2Data);
      setComparisonData(comparison);

    } catch (err) {
      console.error('Error comparing teams:', err);
      setError('Fout bij vergelijken relaties: ' + err.message);
    }

    setComparing(false);
  };

  const renderTeamSelector = (value, onChange, label, excludeId = null) => (
    <div className="team-selector">
      <label className="selector-label">{label}</label>
      <select 
        value={value} 
        onChange={(e) => onChange(e.target.value)}
        className="team-select"
        disabled={comparing}
      >
        <option value="">Selecteer een relatie...</option>
        {teams
          .filter(team => team.id !== excludeId)
          .map(team => (
            <option key={team.id} value={team.id}>
              {team.name} ({team.responseCount} responses, {team.period})
            </option>
          ))
        }
      </select>
    </div>
  );

  const renderLineChart = () => {
    if (!comparisonData) return null;

    const tredeNames = {
      vertrouwen: 'Vertrouwen',
      conflicten: 'Conflicten',
      betrokkenheid: 'Commitment', //Veranderd van Betrokkenheid
      verantwoording: 'Verantwoording',
      resultaten: 'Resultaten'
    };

    const chartData = prepareLineChartData(
      comparisonData.team1.scores,
      comparisonData.team2.scores,
      tredeNames
    );

    return (
      <div className="chart-container">
        <h3>Lencioni Tredes Vergelijking</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData.data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="trede" 
              angle={-45}
              textAnchor="end"
              height={100}
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              domain={[1, 5]} 
              tick={{ fontSize: 12 }}
              label={{ value: 'Score', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip 
              formatter={(value, name) => [`${value}`, name]}
              labelFormatter={(label) => `Trede: ${label}`}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="team1" 
              stroke="#3b82f6" 
              strokeWidth={3}
              name={comparisonData.team1.name}
              dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="team2" 
              stroke="#10b981" 
              strokeWidth={3}
              name={comparisonData.team2.name}
              dot={{ fill: '#10b981', strokeWidth: 2, r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderRadarChart = () => {
    if (!comparisonData) return null;

    const tredeNames = {
      vertrouwen: 'Vertrouwen',
      conflicten: 'Conflicten',
      betrokkenheid: 'Commitment',
      verantwoording: 'Verantwoording',
      resultaten: 'Resultaten'
    };

    const chartData = prepareSpiderChartData(
      comparisonData.team1.scores,
      comparisonData.team2.scores,
      tredeNames
    );

    return (
      <div className="chart-container">
        <h3>Spider Chart Vergelijking</h3>
        <ResponsiveContainer width="100%" height={400}>
          <RadarChart data={chartData.data}>
            <PolarGrid />
            <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
            <PolarRadiusAxis 
              angle={90} 
              domain={[0, 5]} 
              tick={{ fontSize: 10 }}
            />
            <Radar
              name={comparisonData.team1.name}
              dataKey="team1"
              stroke="#3b82f6"
              fill="#3b82f6"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Radar
              name={comparisonData.team2.name}
              dataKey="team2"
              stroke="#10b981"
              fill="#10b981"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Legend />
            <Tooltip />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderComparisonTable = () => {
    if (!comparisonData) return null;

    const tableData = prepareComparisonTableData(
      comparisonData.team1,
      comparisonData.team2,
      comparisonData
    );

    return (
      <div className="comparison-table-container">
        <h3>Gedetailleerde Vergelijking</h3>
        <div className="table-wrapper">
          <table className="comparison-table">
            <thead>
              <tr>
                {tableData.headers.map(header => (
                  <th key={header.key} style={{ width: header.width }}>
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.tableData.map((row, index) => (
                <tr key={index}>
                  <td className="trede-name">{row.trede}</td>
                  <td className="score-cell">{row.team1Score.toFixed(1)}</td>
                  <td className="score-cell">{row.team2Score.toFixed(1)}</td>
                  <td className="difference-cell">
                    <span 
                      className={`difference ${row.formattedDifference.trend}`}
                      style={{ color: row.formattedDifference.color }}
                    >
                      {row.formattedDifference.trendIcon} {row.formattedDifference.displayText}
                    </span>
                  </td>
                  <td className={`trend-cell trend-${row.trend}`}>
                    {row.trend === 'improvement' ? '📈' : 
                     row.trend === 'decline' ? '📉' : '➡️'}
                  </td>
                  <td className={`significance-cell significance-${row.significance}`}>
                    {row.significance === 'significant' ? 'Hoog' :
                     row.significance === 'moderate' ? 'Gemiddeld' : 'Laag'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="table-summary">
          <div className="summary-item">
            <span className="summary-label">Gemiddelde {comparisonData.team1.name}:</span>
            <span className="summary-value">{tableData.summary.team1Average}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Gemiddelde {comparisonData.team2.name}:</span>
            <span className="summary-value">{tableData.summary.team2Average}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Overall trend:</span>
            <span className={`summary-value trend-${tableData.summary.overallTrend}`}>
              {tableData.summary.overallTrend === 'improving' ? '📈 Verbetering' :
               tableData.summary.overallTrend === 'declining' ? '📉 Achteruitgang' : '➡️ Stabiel'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderInsights = () => {
    if (!comparisonData || !comparisonData.insights.length) return null;

    return (
      <div className="insights-section">
        <h3>🎯 Belangrijkste Inzichten</h3>
        <div className="insights-grid">
          {comparisonData.insights.map((insight, index) => (
            <div 
              key={index} 
              className={`insight-card insight-${insight.category} insight-${insight.significance}`}
            >
              <div className="insight-header">
                <span className="insight-icon">
                  {insight.type === 'improvement' ? '📈' :
                   insight.type === 'decline' ? '📉' : '📊'}
                </span>
                <span className="insight-category">
                  {insight.category === 'strength' ? 'Sterkte' :
                   insight.category === 'concern' ? 'Aandachtspunt' : 'Algemeen'}
                </span>
              </div>
              <p className="insight-message">{insight.message}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRecommendations = () => {
    if (!comparisonData || !comparisonData.recommendations.length) return null;

    return (
      <div className="recommendations-section">
        <h3>💡 Aanbevelingen</h3>
        <div className="recommendations-list">
          {comparisonData.recommendations.map((rec, index) => (
            <div key={index} className={`recommendation-card priority-${rec.priority}`}>
              <div className="recommendation-header">
                <span className="priority-badge">
                  {rec.priority === 'critical' ? '🚨 Kritiek' :
                   rec.priority === 'high' ? '⚠️ Hoog' :
                   rec.priority === 'medium' ? '🔶 Gemiddeld' : '💡 Laag'}
                </span>
                <h4 className="recommendation-title">{rec.title}</h4>
              </div>
              <p className="recommendation-message">{rec.message}</p>
              {rec.actions && (
                <div className="recommendation-actions">
                  <strong>Concrete acties:</strong>
                  <ul>
                    {rec.actions.map((action, actionIndex) => (
                      <li key={actionIndex}>{action}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="team-comparison">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Relaties laden voor vergelijking...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="team-comparison">
      <div className="comparison-header">
        <h1>🔄 Relatie Vergelijking</h1>
        <p>Vergelijk de prestaties van je relaties op de 5 Lencioni tredes</p>
        
        <button 
          onClick={() => navigate('/dashboard')}
          className="back-button"
        >
          ← Terug naar Dashboard
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {teams.length >= 2 && (
        <div className="team-selection">
          <div className="selectors-row">
            {renderTeamSelector(
              selectedTeam1, 
              setSelectedTeam1, 
              "Team 1", 
              selectedTeam2
            )}
            
            {renderTeamSelector(
              selectedTeam2, 
              setSelectedTeam2, 
              "Team 2", 
              selectedTeam1
            )}
          </div>

          <button
            onClick={handleCompareTeams}
            disabled={!selectedTeam1 || !selectedTeam2 || selectedTeam1 === selectedTeam2 || comparing}
            className="compare-button"
          >
            {comparing ? (
              <>
                <span className="loading-spinner"></span>
                Vergelijken...
              </>
            ) : (
              '📊 Vergelijk Relaties'
            )}
          </button>
        </div>
      )}

      {comparisonData && (
        <div className="comparison-results">
          {/* Chart Type Toggle */}
          <div className="chart-toggle">
            <button
              onClick={() => setChartView('line')}
              className={`toggle-btn ${chartView === 'line' ? 'active' : ''}`}
            >
              📈 Lijn Grafiek
            </button>
            <button
              onClick={() => setChartView('radar')}
              className={`toggle-btn ${chartView === 'radar' ? 'active' : ''}`}
            >
              🕸️ Spider Chart
            </button>
            <button
              onClick={() => setChartView('table')}
              className={`toggle-btn ${chartView === 'table' ? 'active' : ''}`}
            >
              📋 Tabel
            </button>
          </div>

          {/* Chart Display */}
          <div className="charts-section">
            {chartView === 'line' && renderLineChart()}
            {chartView === 'radar' && renderRadarChart()}
            {chartView === 'table' && renderComparisonTable()}
          </div>

          {/* Insights and Recommendations */}
          {renderInsights()}
          {renderRecommendations()}
        </div>
      )}

      {teams.length < 2 && !loading && (
        <div className="no-teams-message">
          <div className="no-teams-icon">📊</div>
          <h3>Niet genoeg relaties voor vergelijking</h3>
          <p>
            Je hebt minimaal 2 relaties met voltooide responses nodig om een vergelijking te maken.
          </p>
          <button 
            onClick={() => navigate('/create-team')}
            className="create-team-button"
          >
            ➕ Maak een Nieuwe Relatie
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamComparison;